import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Empty, Progress, Space, Spin, Tag, Typography } from 'antd';
import {
  LeftOutlined,
  DatabaseOutlined,
  RightOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis
} from 'recharts';
import api from '../../../services/api';

const { Text } = Typography;
const COVERAGE_DAYS = 30;

const JOB_STATUS_META = {
  success: { label: 'Done', color: '#22c55e' },
  skipped: { label: 'Skipped', color: '#94a3b8' },
  running: { label: 'Running', color: '#3b82f6' },
  partial: { label: 'Partial', color: '#f59e0b' },
  failed: { label: 'Failed', color: '#ef4444' },
  pending: { label: 'Pending', color: '#e2e8f0' }
};

const COVERAGE_STAGES = [
  { key: 'skuCrawlStatus', label: 'SKU crawl', shortLabel: 'SKU' },
  { key: 'dailyMetricsStatus', label: 'Daily metrics', shortLabel: 'Metrics' },
  { key: 'rollingCorrectionStatus', label: 'Rolling correction', shortLabel: 'Rolling' },
  { key: 'periodCacheStatus', label: 'Period cache', shortLabel: 'Cache' }
];

const PERIOD_CACHE_META = {
  fresh: { label: 'Fresh', tag: 'green', tone: 'period-cache-fresh' },
  running: { label: 'Running', tag: 'processing', tone: 'period-cache-running' },
  failed: { label: 'Failed', tag: 'red', tone: 'period-cache-failed' },
  stale: { label: 'Stale', tag: 'gold', tone: 'period-cache-stale' },
  not_tracked: { label: 'Not tracked', tag: 'default', tone: 'period-cache-stale' }
};

const DAILY_JOB_TIMELINE = [
  {
    key: 'sku-crawl',
    label: 'SKU/listing crawl',
    time: '00:10',
    minutes: 10,
    note: 'full crawl, may run 2-6h',
    lane: 'top',
    type: 'job',
    align: 'start'
  },
  {
    key: 'daily-metrics',
    label: 'Daily metrics',
    time: '15:30',
    minutes: (15 * 60) + 30,
    note: 'snapshot after LA close',
    lane: 'top',
    type: 'job',
    align: 'center'
  },
  {
    key: 'rolling-correct',
    label: 'Rolling correction',
    time: '17:30',
    minutes: (17 * 60) + 30,
    note: 'sales/category 7d',
    lane: 'bottom',
    type: 'job',
    align: 'end'
  },
  {
    key: 'period-cache',
    label: 'Period cache',
    time: '18:30',
    minutes: (18 * 60) + 30,
    note: 'refresh 7d/30d/90d',
    lane: 'top',
    type: 'job',
    align: 'end'
  }
];

const numberFormat = new Intl.NumberFormat('en-US');

function formatNumber(value) {
  return numberFormat.format(Number(value || 0));
}

function formatShortDate(dateString) {
  if (!dateString) return '-';
  const [, month, day] = String(dateString).split('-');
  return `${month}/${day}`;
}

function getTodayInLosAngeles() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function shiftDate(dateString, days) {
  return dayjs(dateString).add(days, 'day').format('YYYY-MM-DD');
}

function formatTimeFromMinutes(minutes) {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function getLosAngelesOffsetHours(dateString) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      timeZoneName: 'shortOffset'
    }).formatToParts(new Date(`${dateString}T12:00:00Z`));
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || '';
    const match = timeZoneName.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
    if (match) return parseInt(match[1], 10);
  } catch (error) {
    // Fall back to the common LA DST window when shortOffset is unavailable.
  }

  const month = Number(String(dateString || '').slice(5, 7));
  return month >= 3 && month <= 11 ? -7 : -8;
}

function getLosAngelesCloseInfo(metricDate) {
  const safeMetricDate = metricDate || getTodayInLosAngeles();
  const closeDate = shiftDate(safeMetricDate, 1);
  const offsetHours = getLosAngelesOffsetHours(closeDate);
  const vnHour = 7 - offsetHours;
  const vnMinutes = vnHour * 60;

  return {
    metricDate: safeMetricDate,
    closeDate,
    offsetLabel: offsetHours === -7 ? 'PDT' : 'PST',
    time: formatTimeFromMinutes(vnMinutes),
    minutes: vnMinutes,
    note: `LA ${safeMetricDate} closes at ${closeDate} ${formatTimeFromMinutes(vnMinutes)} VN`
  };
}

function getTimeZoneNowLabel(timeZone) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short'
    }).format(new Date()).replace(',', '');
  } catch (error) {
    return '-';
  }
}

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatPeriods(periods = []) {
  const values = periods.map(period => Number(period)).filter(Boolean);
  return values.length ? values.map(period => `${period}d`).join(', ') : '-';
}

function normalizeJobStatus(status) {
  return JOB_STATUS_META[status] ? status : 'pending';
}

function getJobStatusMeta(status) {
  return JOB_STATUS_META[normalizeJobStatus(status)];
}

function isCompletedJobStatus(status) {
  return ['success', 'skipped'].includes(status);
}

function getStageDetail(row, stageKey) {
  if (stageKey === 'skuCrawlStatus') {
    return formatDateTime(row.skuCrawlFinishedAt || row.skuCrawlStartedAt);
  }
  if (stageKey === 'dailyMetricsStatus') {
    return `${formatNumber(row.metricCount)} / ${formatNumber(row.activeSkuCount)}`;
  }
  if (stageKey === 'rollingCorrectionStatus') {
    return `${formatNumber(row.correctionCount)} / ${formatNumber(row.activeSkuCount)}`;
  }
  return formatDateTime(row.periodCacheFinishedAt || row.periodCacheStartedAt);
}

function getPeriodCacheMeta(cache) {
  if (!cache?.tracked) return PERIOD_CACHE_META.not_tracked;
  if (cache.status === 'running') return PERIOD_CACHE_META.running;
  if (cache.status === 'failed') return PERIOD_CACHE_META.failed;
  if (cache.fresh) return PERIOD_CACHE_META.fresh;
  return PERIOD_CACHE_META.stale;
}

function getDateTicks(dates = []) {
  return dates.map((_, index) => index);
}

function CoverageMetric({ label, value, suffix, detail, tone = 'default' }) {
  return (
    <div className={`sync-coverage-metric sync-coverage-metric-${tone}`}>
      <Text type="secondary">{label}</Text>
      <strong>{value}{suffix || ''}</strong>
      {detail && <div className="sync-coverage-metric-detail">{detail}</div>}
    </div>
  );
}

function JobRunTimeline({ metricDate }) {
  const laClose = getLosAngelesCloseInfo(metricDate);
  const items = [
    DAILY_JOB_TIMELINE[0],
    {
      key: 'la-close',
      label: 'LA day closes',
      time: laClose.time,
      minutes: laClose.minutes,
      note: `00:00 LA ${laClose.offsetLabel}`,
      lane: 'bottom',
      type: 'milestone',
      align: 'center'
    },
    ...DAILY_JOB_TIMELINE.slice(1)
  ];

  return (
    <div className="sync-job-timeline">
      <div className="sync-job-timeline-header">
        <Text strong>Daily job timeline</Text>
        <div className="sync-job-timeline-meta">
          <span>LA now: {getTimeZoneNowLabel('America/Los_Angeles')}</span>
          <span>VN now: {getTimeZoneNowLabel('Asia/Ho_Chi_Minh')}</span>
          <span>{laClose.note}</span>
        </div>
      </div>
      <div className="sync-job-timeline-scroll">
        <div className="sync-job-timeline-track">
          <div className="sync-job-timeline-line" />
          {items.map(item => (
            <div
              key={item.key}
              className={`sync-job-timeline-item sync-job-timeline-item-${item.lane} sync-job-timeline-item-${item.type} sync-job-timeline-item-${item.align || 'center'}`}
              style={{ left: `${(item.minutes / 1440) * 100}%` }}
            >
              <div className="sync-job-timeline-label">
                <strong>{item.time}</strong>
                <span>{item.label}</span>
                <small>{item.note}</small>
              </div>
              <span className="sync-job-timeline-marker" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CoverageTooltipContent({ row }) {
  if (!row) return null;
  const stageStatuses = COVERAGE_STAGES.map(stage => normalizeJobStatus(row[stage.key]));
  const completedStages = stageStatuses.filter(isCompletedJobStatus).length;
  const progressTagColor = stageStatuses.includes('failed')
    ? 'red'
    : completedStages === COVERAGE_STAGES.length
      ? 'green'
      : 'blue';
  return (
    <div className="sync-coverage-chart-tooltip">
      <div className="sync-coverage-tooltip-header">
        <div>
          <Text strong>{row.sellerName}</Text>
          <Text type="secondary"> · {row.metricDate}</Text>
        </div>
        <Tag color={progressTagColor}>
          {completedStages}/4
        </Tag>
      </div>
      <div className="sync-coverage-tooltip-jobs">
        {COVERAGE_STAGES.map(stage => {
          const status = normalizeJobStatus(row[stage.key]);
          const statusMeta = getJobStatusMeta(status);
          return (
            <div className="sync-coverage-tooltip-job" key={stage.key}>
              <span
                className="sync-coverage-tooltip-status"
                style={{ backgroundColor: statusMeta.color }}
              />
              <Text>{stage.label}</Text>
              <span className="sync-coverage-tooltip-job-value">
                <Text style={{ color: status === 'pending' ? '#94a3b8' : statusMeta.color }}>
                  {statusMeta.label}
                </Text>
                <Text type="secondary"> · {getStageDetail(row, stage.key)}</Text>
              </span>
            </div>
          );
        })}
      </div>
      <div className="sync-coverage-tooltip-baseline">
        <Text type="secondary">SKU baseline</Text>
        <Text>
          {formatNumber(row.activeSkuCount)}
          {row.baselineMetricDate ? ` · ${row.baselineMetricDate}` : ''}
        </Text>
      </div>
    </div>
  );
}

function HeatmapCell(props) {
  const { cx, cy, payload, onCellHover, onCellLeave } = props;
  const width = 20;
  const height = 16;
  const gap = 1;
  const padding = 2;
  const segmentWidth = (width - (padding * 2) - (gap * 3)) / 4;
  const statuses = COVERAGE_STAGES.map(stage => normalizeJobStatus(payload[stage.key]));
  const allComplete = statuses.every(isCompletedJobStatus);
  const hasProgress = statuses.some(status => status !== 'pending');
  const borderColor = allComplete
    ? JOB_STATUS_META.success.color
    : hasProgress
      ? JOB_STATUS_META.partial.color
      : '#cbd5e1';
  const handleHover = (event) => {
    onCellHover?.(payload, event);
  };

  return (
    <g
      onMouseEnter={handleHover}
      onMouseMove={handleHover}
      onMouseLeave={onCellLeave}
      style={{ cursor: 'pointer' }}
    >
      <rect
        x={cx - width / 2}
        y={cy - height / 2}
        width={width}
        height={height}
        rx={3}
        ry={3}
        fill="#ffffff"
        stroke={borderColor}
        strokeWidth={1}
      />
      {statuses.map((status, index) => (
        <rect
          key={`${payload.sellerId}-${payload.metricDate}-${COVERAGE_STAGES[index].key}`}
          x={(cx - width / 2) + padding + (index * (segmentWidth + gap))}
          y={(cy - height / 2) + padding}
          width={segmentWidth}
          height={height - (padding * 2)}
          rx={1}
          ry={1}
          fill={getJobStatusMeta(status).color}
        />
      ))}
    </g>
  );
}

function SyncCoveragePanel({ onOpenMetricsBackfill }) {
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [coverageDateTo, setCoverageDateTo] = useState(() => getTodayInLosAngeles());
  const todayInLosAngeles = useMemo(() => getTodayInLosAngeles(), []);

  const fetchCoverage = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        days: String(COVERAGE_DAYS),
        dateTo: coverageDateTo
      });
      const response = await api.get(`/inventory/listings/sync-coverage?${params.toString()}`);
      setCoverage(response.data);
    } catch (error) {
      console.error('Error fetching sync coverage:', error);
      setCoverage(null);
    } finally {
      setLoading(false);
    }
  }, [coverageDateTo]);

  useEffect(() => {
    fetchCoverage();
  }, [fetchCoverage]);

  const rowsBySeller = useMemo(() => {
    if (!coverage?.rows) return [];
    const grouped = new Map();
    coverage.rows.forEach(row => {
      if (!grouped.has(row.sellerId)) {
        grouped.set(row.sellerId, {
          sellerId: row.sellerId,
          sellerName: row.sellerName,
          accountStatus: row.accountStatus,
          activeSkuCount: row.activeSkuCount,
          days: []
        });
      }
      grouped.get(row.sellerId).days.push(row);
    });
    return Array.from(grouped.values());
  }, [coverage]);

  const sellerByChartY = useMemo(() => {
    const map = new Map();
    rowsBySeller.forEach((seller, index) => {
      map.set(rowsBySeller.length - index - 1, seller);
    });
    return map;
  }, [rowsBySeller]);

  const heatmapData = useMemo(() => {
    const dates = coverage?.dates || [];
    const dateIndexMap = new Map(dates.map((date, index) => [date, index]));
    return rowsBySeller.flatMap((seller, sellerIndex) => (
      seller.days.map(row => ({
        ...row,
        x: dateIndexMap.get(row.metricDate),
        y: rowsBySeller.length - sellerIndex - 1
      }))
    )).filter(row => row.x !== undefined);
  }, [coverage, rowsBySeller]);

  const dateTicks = useMemo(() => getDateTicks(coverage?.dates || []), [coverage]);
  const chartHeight = Math.max(170, rowsBySeller.length * 28 + 42);
  const chartWidth = Math.max(980, ((coverage?.dates || []).length * 56) + 230);
  const summary = coverage?.summary || {};
  const salesPeriodCache = coverage?.salesPeriodCache || {};
  const periodCacheMeta = getPeriodCacheMeta(salesPeriodCache);
  const handleCellHover = useCallback((row, event) => {
    const tooltipWidth = 320;
    const tooltipHeight = 340;
    const left = Math.min(event.clientX + 14, window.innerWidth - tooltipWidth - 12);
    const top = Math.min(event.clientY + 14, window.innerHeight - tooltipHeight - 12);
    setActiveTooltip({
      row,
      left: Math.max(12, left),
      top: Math.max(12, top)
    });
  }, []);
  const handleCellLeave = useCallback(() => {
    setActiveTooltip(null);
  }, []);
  const handleShiftCoverageWindow = useCallback((days) => {
    setCoverageDateTo(prev => {
      const next = shiftDate(prev, days);
      return next > todayInLosAngeles ? todayInLosAngeles : next;
    });
  }, [todayInLosAngeles]);
  const renderHeatmapCell = useCallback((props) => (
    <HeatmapCell
      {...props}
      onCellHover={handleCellHover}
      onCellLeave={handleCellLeave}
    />
  ), [handleCellHover, handleCellLeave]);

  return (
    <section className="sync-coverage-section">
      <div className="sync-coverage-header">
        <div>
          <Space size={8}>
            {coverage?.timezone && <Tag>{coverage.timezone}</Tag>}
            {summary.metricDate && <Tag color="blue">{summary.metricDate}</Tag>}
            {coverage?.salesPeriodCache && (
              <Tag color={periodCacheMeta.tag}>
                Period cache: {periodCacheMeta.label}
              </Tag>
            )}
          </Space>
          <div className="sync-coverage-subtitle">
            Daily job completion by seller for the selected {COVERAGE_DAYS}-day metric window.
          </div>
        </div>
        <Space size={8}>
          <Button
            size="small"
            icon={<LeftOutlined />}
            onClick={() => handleShiftCoverageWindow(-COVERAGE_DAYS)}
            title={`Previous ${COVERAGE_DAYS} days`}
          />
          <DatePicker
            size="small"
            value={dayjs(coverageDateTo)}
            allowClear={false}
            disabledDate={(current) => current && current.format('YYYY-MM-DD') > todayInLosAngeles}
            onChange={(date) => {
              if (date) setCoverageDateTo(date.format('YYYY-MM-DD'));
            }}
          />
          <Button
            size="small"
            icon={<RightOutlined />}
            onClick={() => handleShiftCoverageWindow(COVERAGE_DAYS)}
            disabled={coverageDateTo >= todayInLosAngeles}
            title={`Next ${COVERAGE_DAYS} days`}
          />
          <Button
            size="small"
            icon={<DatabaseOutlined />}
            onClick={onOpenMetricsBackfill}
            title="Trigger collect-sku-daily-metrics with manual options"
          >
            Trigger Metrics
          </Button>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchCoverage} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      <JobRunTimeline metricDate={summary.metricDate || coverageDateTo} />

      <Spin spinning={loading && !coverage}>
        {!coverage ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No coverage data" />
        ) : (
          <>
            <div className="sync-coverage-summary">
              <CoverageMetric label="SKU crawl" value={`${summary.skuCrawlDone || 0}/${summary.accountCount || 0}`} />
              <CoverageMetric label="Daily metrics" value={`${summary.dailyMetricsDone || 0}/${summary.accountCount || 0}`} />
              <CoverageMetric label="Rolling correction" value={`${summary.rollingCorrectionDone || 0}/${summary.accountCount || 0}`} tone="rank" />
              <CoverageMetric label="Period cache" value={`${summary.periodCacheDone || 0}/${summary.accountCount || 0}`} />
              <CoverageMetric label="Active SKU" value={formatNumber(summary.activeSkuCount)} />
              <CoverageMetric label="Accounts done" value={`${summary.completeAccounts || 0}/${summary.accountCount || 0}`} />
              <CoverageMetric
                label="Cache status"
                value={periodCacheMeta.label}
                tone={periodCacheMeta.tone}
                detail={`${formatPeriods(salesPeriodCache.cachedPeriods || salesPeriodCache.periods)} | ${salesPeriodCache.metricDate || salesPeriodCache.expectedMetricDate || '-'}`}
              />
            </div>

            <div className="sync-coverage-progress-row">
              <Progress
                percent={Number(summary.snapshotCoverage || 0)}
                size="small"
                status={Number(summary.snapshotCoverage || 0) >= 99 ? 'success' : 'active'}
                showInfo={false}
              />
              <Text type="secondary">
                {summary.dailyMetricsDone || 0} / {summary.accountCount || 0} sellers finished daily metrics on {summary.metricDate}
              </Text>
            </div>

            <div className="sync-coverage-chart">
              <div className="sync-coverage-chart-inner" style={{ width: chartWidth }}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <ScatterChart margin={{ top: 8, right: 16, bottom: 8, left: 10 }}>
                    <CartesianGrid stroke="#eef2f7" vertical={false} />
                    <XAxis
                      type="number"
                      dataKey="x"
                      domain={[-0.5, Math.max((coverage.dates || []).length - 0.5, 0.5)]}
                      ticks={dateTicks}
                      tickFormatter={(index) => formatShortDate((coverage.dates || [])[index])}
                      interval={0}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      domain={[-0.5, Math.max(rowsBySeller.length - 0.5, 0.5)]}
                      ticks={rowsBySeller.map((_, index) => index)}
                      tickFormatter={(index) => sellerByChartY.get(index)?.sellerName || ''}
                      width={178}
                      tick={{ fontSize: 12, fill: '#475569' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Scatter data={heatmapData} shape={renderHeatmapCell} isAnimationActive={false} />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>
            {activeTooltip && (
              <div
                className="sync-coverage-tooltip-layer"
                style={{ left: activeTooltip.left, top: activeTooltip.top }}
              >
                <CoverageTooltipContent row={activeTooltip.row} />
              </div>
            )}

            <div className="sync-coverage-legend">
              <Space size={5}>
                <Text type="secondary">Order</Text>
                <Text>{COVERAGE_STAGES.map(stage => stage.shortLabel).join(' · ')}</Text>
              </Space>
              {['success', 'running', 'partial', 'failed', 'pending'].map(status => {
                const meta = JOB_STATUS_META[status];
                return (
                  <Space size={4} key={status}>
                    <span className="sync-coverage-legend-dot" style={{ backgroundColor: meta.color }} />
                    <Text type="secondary">{meta.label}</Text>
                  </Space>
                );
              })}
            </div>
          </>
        )}
      </Spin>
    </section>
  );
}

export default SyncCoveragePanel;
